import { getApiBaseUrl } from "../../utils/ssoProvider";
import React, { useState, useEffect } from "react";
import PopupModal from "../PopupModal";
import axiosInstance from "../../services/axiosInstance";
import { toast } from "react-toastify";
import { DEFAULT_LABEL_BACKGROUND_COLOR, DEFAULT_LABEL_TEXT_COLOR } from "../../constants/colors";

const API_URL = getApiBaseUrl();

const CreateLabelModal = ({ isOpen, onClose, editingLabel = null, onSuccess }) => {
  const [labelName, setLabelName] = useState("");
  const [backgroundColor, setBackgroundColor] = useState(DEFAULT_LABEL_BACKGROUND_COLOR);
  const [textColor, setTextColor] = useState(DEFAULT_LABEL_TEXT_COLOR);
  const [saving, setSaving] = useState(false);
  const MAX_LENGTH = 50;

  useEffect(() => {
    if (editingLabel) {
      setLabelName(editingLabel.name);
      setBackgroundColor(editingLabel.background_color || editingLabel.color || DEFAULT_LABEL_BACKGROUND_COLOR);
      setTextColor(editingLabel.text_color || DEFAULT_LABEL_TEXT_COLOR);
    } else {
      setLabelName("");
      setBackgroundColor(DEFAULT_LABEL_BACKGROUND_COLOR);
      setTextColor(DEFAULT_LABEL_TEXT_COLOR);
    }
  }, [editingLabel, isOpen]);

  const handleClose = () => {
    if (saving) return;
    setLabelName("");
    setBackgroundColor(DEFAULT_LABEL_BACKGROUND_COLOR);
    setTextColor(DEFAULT_LABEL_TEXT_COLOR);
    onClose();
  };

  const saveLabel = async () => {
    if (!labelName.trim()) return toast.error("Label name is required");
    if (labelName.length > MAX_LENGTH) return toast.error(`Max ${MAX_LENGTH} characters allowed`);

    setSaving(true);
    try {
      const payload = {
        name: labelName.trim(),
        background_color: backgroundColor,
        text_color: textColor,
      };

      if (editingLabel) {
        await axiosInstance.patch(`${API_URL}/v1/store/labels/${editingLabel.id}`, payload);
        toast.success("Label updated");
      } else {
        await axiosInstance.post(`${API_URL}/v1/store/labels`, payload);
        toast.success("Label created");
      }
      setLabelName("");
      setBackgroundColor(DEFAULT_LABEL_BACKGROUND_COLOR);
      setTextColor(DEFAULT_LABEL_TEXT_COLOR);
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save label");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <PopupModal closeModal={handleClose} className="bg-white w-full max-w-md">
      <div>
        <h3 className="font-semibold text-lg">{editingLabel ? "Edit Label" : "New Label"}</h3>
        <p className="text-sm text-gray-500 mt-1">Enter new label name</p>

        <div className="mt-4 space-y-4">
          {/* Label Name Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Label Name</label>
            <input
              value={labelName}
              onChange={e => setLabelName(e.target.value)}
              maxLength={MAX_LENGTH}
              disabled={saving}
              data-testid="label-name-input"
              className={`w-full h-11 px-2 border rounded-md focus:border-[#39F6A3] focus:outline-none ${
                labelName.length === MAX_LENGTH ? "border-red-500" : ""
              }`}
              placeholder="Enter label name"
              onKeyDown={e => {
                if (e.key === "Enter") {
                  saveLabel();
                }
              }}
            />
            <div className="flex justify-between items-center mt-1">
              <span
                className={`text-xs ${
                  labelName.length >= MAX_LENGTH
                    ? "text-red-600 font-medium"
                    : labelName.length >= MAX_LENGTH - 10
                      ? "text-orange-600"
                      : "text-gray-500"
                }`}
              >
                {labelName.length >= MAX_LENGTH && "Maximum character limit reached"}
              </span>
              <span
                className={`text-xs ${
                  labelName.length >= MAX_LENGTH
                    ? "text-red-600 font-medium"
                    : labelName.length >= MAX_LENGTH - 10
                      ? "text-orange-600"
                      : "text-gray-500"
                }`}
              >
                {labelName.length}/{MAX_LENGTH}
              </span>
            </div>
          </div>

          {/* Color Pickers */}
          <div className="space-y-4">
            {/* Background Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
              <div className="flex items-center border rounded-md h-[44px]">
                <input
                  type="text"
                  value={backgroundColor}
                  onChange={e => setBackgroundColor(e.target.value)}
                  disabled={saving}
                  data-testid="background-color-input"
                  className="flex-1 p-2 bg-transparent border-none text-[#868CA2] focus:outline-none uppercase"
                  placeholder="#2B2E3A"
                  maxLength={7}
                />
                <input
                  type="color"
                  value={backgroundColor}
                  onChange={e => setBackgroundColor(e.target.value)}
                  disabled={saving}
                  data-testid="background-color-picker"
                  className="w-[44px] h-[44px] border-none cursor-pointer rounded-md"
                />
              </div>
            </div>

            {/* Text Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
              <div className="flex items-center border rounded-md h-[44px]">
                <input
                  type="text"
                  value={textColor}
                  onChange={e => setTextColor(e.target.value)}
                  disabled={saving}
                  data-testid="text-color-input"
                  className="flex-1 p-2 bg-transparent border-none text-[#868CA2] focus:outline-none uppercase"
                  placeholder="#96F3C3"
                  maxLength={7}
                />
                <input
                  type="color"
                  value={textColor}
                  onChange={e => setTextColor(e.target.value)}
                  disabled={saving}
                  data-testid="text-color-picker"
                  className="w-[44px] h-[44px] border-none cursor-pointer rounded-md"
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-sm font-medium"
                style={{
                  backgroundColor: backgroundColor,
                  color: textColor,
                }}
              >
                {labelName || "Label Preview"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <button
          onClick={handleClose}
          data-testid="cancel-label-button"
          className="px-4 py-2 border rounded-md bg-[#F4F4F4]"
          disabled={saving}
        >
          Cancel
        </button>
        <button
          onClick={saveLabel}
          data-testid="save-label-button"
          className="px-4 py-2 bg-[#0C0D17] text-[#96F3C3] rounded-md"
          disabled={saving}
        >
          {saving ? "Saving..." : editingLabel ? "Update" : "Create"}
        </button>
      </div>
    </PopupModal>
  );
};

export default CreateLabelModal;
